import React from 'react';
import { getCurrentUserId } from '../lib/currentUser';

/**
 * Top-level React error boundary.
 *
 * Renders a plain, restrained fallback if any child throws so a single unguarded
 * property access no longer blanks the whole page. On catch, fire-and-forget the
 * error to `/api/errors/report` so we get visibility without waiting for users
 * to email support.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Root ErrorBoundary caught:', error, info?.componentStack);
    }
    this.reportError(error, info);
  }

  reportError = (error, info) => {
    // Fire-and-forget: never block the fallback UI, swallow any transport failure.
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const body = JSON.stringify({
        message: String(error?.message || error || 'Unknown error').slice(0, 1000),
        stack: String(info?.componentStack || error?.stack || '').slice(0, 8000),
        url: window.location.href.slice(0, 500),
        timestamp: new Date().toISOString(),
        user_id: getCurrentUserId(),
      });
      // fetch with keepalive so the request survives an immediate reload.
      fetch(`${base}/api/errors/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
        credentials: 'omit',
      }).catch(() => {});
    } catch {
      // silent
    }
  };

  handleReload = () => {
    this.setState({ hasError: false });
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        style={{
          minHeight: '100vh',
          background: '#F6F0E3',
          color: '#211C16',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          fontFamily: '"Work Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
        data-testid="root-error-boundary"
      >
        <div style={{ maxWidth: 420, width: '100%', textAlign: 'left' }}>
          <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.01em', margin: 0 }}>
            Something went wrong
          </h1>
          <p style={{ fontSize: 14, opacity: 0.7, margin: '12px 0 24px' }}>
            Your data is safe — try refreshing.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 40,
              padding: '0 20px',
              borderRadius: 999,
              background: '#211C16',
              color: '#F6F0E3',
              border: 'none',
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
            }}
            data-testid="error-boundary-refresh-btn"
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
